'use client';

import useSWR, { type SWRConfiguration } from 'swr';
import customFetch from '@/lib/utils/fetch';

export interface WorldCupPlayerStat {
  player_name: string;
  db_name: string | null;
  season_year: number | null;
  goals: number | null;
  assists: number | null;
  image_url: string | null;
}

export type WorldCupPlayerStatsData = WorldCupPlayerStat | WorldCupPlayerStat[];

export interface WorldCupPlayerStatsResponse {
  success: boolean;
  data: WorldCupPlayerStatsData;
  message?: string;
}

export interface FetchWorldCupPlayerStatsOptions {
  player?: string | null;
  players?: string[] | null;
}

export interface UseWorldCupPlayerStatsOptions extends FetchWorldCupPlayerStatsOptions {
  enabled?: boolean;
  swr?: SWRConfiguration<WorldCupPlayerStatsResponse, Error>;
}

type WorldCupPlayerStatsKey = readonly [
  'world-cup-player-stats',
  {
    player: string | null;
    players: string[];
  },
];

function normalizePlayerName(player?: string | null) {
  const trimmed = player?.trim();
  return trimmed ? trimmed : null;
}

function normalizePlayers(players?: string[] | null) {
  return Array.from(
    new Set(
      (players ?? [])
        .map((player) => normalizePlayerName(player))
        .filter((player): player is string => Boolean(player)),
    ),
  );
}

function buildRequestBody({ player, players }: FetchWorldCupPlayerStatsOptions) {
  const normalizedPlayers = normalizePlayers(players);
  const normalizedPlayer = normalizePlayerName(player);

  if (normalizedPlayers.length > 0) {
    return { players: normalizedPlayers };
  }

  if (normalizedPlayer) {
    return { player: normalizedPlayer };
  }

  throw new Error('player or players is required');
}

export async function fetchWorldCupPlayerStats(options: FetchWorldCupPlayerStatsOptions) {
  const response = await customFetch('/api/world-cup/player/stats', {
    method: 'POST',
    body: JSON.stringify(buildRequestBody(options)),
  });

  if (!(response instanceof Response)) {
    throw response instanceof Error
      ? response
      : new Error('Failed to fetch World Cup player stats');
  }

  const result = (await response.json()) as WorldCupPlayerStatsResponse;

  if (!result.success) {
    throw new Error(result.message || 'Failed to fetch World Cup player stats');
  }

  return result;
}

export function useWorldCupPlayerStats(options: UseWorldCupPlayerStatsOptions = {}) {
  const player = normalizePlayerName(options.player);
  const players = normalizePlayers(options.players);
  const enabled = options.enabled ?? true;

  const swrKey: WorldCupPlayerStatsKey | null =
    enabled && (player || players.length > 0)
      ? [
          'world-cup-player-stats',
          {
            player,
            players,
          },
        ]
      : null;

  const { data, error, isLoading, isValidating, mutate } = useSWR<
    WorldCupPlayerStatsResponse,
    Error
  >(swrKey, ([, params]: WorldCupPlayerStatsKey) => fetchWorldCupPlayerStats(params), {
    revalidateOnFocus: true,
    keepPreviousData: true,
    ...options.swr,
  });

  const stats = Array.isArray(data?.data) ? data.data : data?.data ? [data.data] : [];
  const stat = Array.isArray(data?.data) ? null : (data?.data ?? null);
  const statsByPlayer = new Map(stats.map((item) => [item.player_name, item]));

  return {
    data: data ?? null,
    stat,
    stats,
    statsByPlayer,
    error,
    isLoading,
    isValidating,
    refresh: mutate,
  };
}

export default useWorldCupPlayerStats;
